import { useEffect, useState } from 'react'
import { RefundTOS } from './RefundCalculatedSteps/RefundTOS'
import { AdvancePaymentDetails } from './RefundCalculatedSteps/AdvancePaymentDetails'
import { AdvancePaymentBankVerification } from './RefundCalculatedSteps/AdvancePaymentBankVerification'
import { RefundOptions } from './RefundCalculatedSteps/RefundOptions'
import { AdvanceFundingApplication, company, customerData, ValidiFiBankVerification } from '../../utilities/CustomerData'
import { RefundGenerateInvoice } from './RefundCalculatedSteps/RefundGenerateInvoice'
import { refreshAccessToken } from '../../utilities/extensions'
import { RefundShippo } from './RefundCalculatedSteps/RefundShippo'
import { useNavigate } from 'react-router-dom'
import { apiURL } from '../../utilities/URLs'
import { RefundInfo } from './RefundCalculatedSteps/RefundInfo'
import { IDMEVerification } from '../IDMEVerification'
import posthog from 'posthog-js'
import { RefundShippingOptions } from './RefundCalculatedSteps/RefundShippingOptions'

export default function RefundCalculated({ setReload }) {
  const navigate = useNavigate()

  const [application, setApplication] = useState(customerData.customerAdvanceFunding)
  const [step, setStep] = useState(0)
  const [refundType, setRefundType] = useState('')
  const [refundRate, setRefundRate] = useState(0)
  const [lienAmount, setLienAmount] = useState(customerData.customerAdvanceFunding.lienAmount)
  const [interestAmount, setInterestAmount] = useState(customerData.customerAdvanceFunding.interestAmount)
  const [loanInterestAmount, setLoanInterestAmount] = useState(customerData.customerAdvanceFunding.loanInterestAmount)
  const [code, setCode] = useState(new URLSearchParams(window.location.search).get('code'))
  const [showKYC, setShowKYC] = useState(customerData.customerVerificationData.verificationId ? false : true)
  const [shippingOption, setShippingOption] = useState('')

  useEffect(() => {
    const handleExchangeIDMECode = async () => {
      const form = new FormData()
      form.append('codeInfo', JSON.stringify({
        customerId: customerData.customerLeadData.leadContact.contactId,
        code: code
      }))

      const exchangeCodeResponse = await fetch(`${apiURL}/api/exchangeCodeIDME`, {
        method: 'POST',
        body: form
      })
      const exchangeCodeData = await exchangeCodeResponse.json()
      if(exchangeCodeData.response.error)
        console.log('RECONNECT')
    }

    if(code) {
      handleExchangeIDMECode()
      navigate('/refundCalculated')
      setShowKYC(false)
      setStep(1)
    }
  }, [code])

  const handleAdvanceChosenStatus = async () => {
    const form = new FormData()
    form.append('upsertInfo', JSON.stringify({
      customer_id: 1,
      customerId: customerData.customerLeadData.leadContact.contactId,
      first_name: customerData.customerLeadData.leadContact.contactFirstName,
      last_name: customerData.customerLeadData.leadContact.contactLastName,
      email: customerData.customerLeadData.leadContact.contactEmail,
      current_status: 'advance_chosen'
    }))

    const upsertCustomerResponse = await fetch(`${apiURL}/api/upsertCustomerStatus`, {
      method: 'PUT',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const upsertCustomerData = await upsertCustomerResponse.json()
    if(upsertCustomerData.response.error) {
      if(upsertCustomerData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleAdvanceChosenStatus() : navigate('/', { state: { expired: true } })
      } else
        console.error(upsertCustomerData.response.error)
    }
  }

  const handleSelectRefundType = async (refundType, refundRate) => {
    setRefundType(refundType)
    setRefundRate(refundRate)

    setStep(customerData.customerTOSData.tosId ? step + 2 : step + 1)
    if(refundType === 'Advance Funding')
      await handleAdvanceChosenStatus()
  }

  const handleInitApplication = async (_validiFiBankVerification) => {
    const newValidiFiBankVerification = new ValidiFiBankVerification(_validiFiBankVerification)

    const form = new FormData()
    form.append('applicationInfo', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      customerId: customerData.customerLeadData.leadContact.contactId,
      refundAmount: customerData.customerLeadData.leadAmount,
      creditAmount: customerData.customerLeadData.leadAmount - lienAmount,
      interestAmount: interestAmount,
      loanInterestAmount: loanInterestAmount,
      lienAmount: lienAmount,
      newValidiFiBankVerification
    }))

    const createCustomerApplicationResponse = await fetch(`${apiURL}/api/createAdvanceApplication`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const createCustomerApplicationData = await createCustomerApplicationResponse.json()
    if (createCustomerApplicationData.response.error) {
      if (createCustomerApplicationData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleInitApplication() : navigate('/', { state: { expired: true } })
      } else
        console.error(createCustomerApplicationData.response.error)
    } else {
      const advanceFundingApplication = new AdvanceFundingApplication(createCustomerApplicationData.response.advanceFundingApplication)
      customerData.customerAdvanceFunding = advanceFundingApplication
      setApplication(advanceFundingApplication)
    }
  }

  useEffect(() => {
    document.title = `${company.companyName} | Refund Calculated`
    posthog.startSessionRecording()

    return () => {
      posthog.stopSessionRecording()
    }
  }, [])

  return (
    <div className='w-screen flex flex-col items-center mt-4 font-robo'>
      {step === 0 ? (
        <RefundInfo
          step={step}
          setStep={setStep}
        />
      ) : step === 1 ? (
        showKYC ? (
          <IDMEVerification />
        ) : (
          <RefundOptions
            applicationId={application.applicationId}
            setInterestAmount={setInterestAmount}
            setLienAmount={setLienAmount}
            handleSelectRefundType={handleSelectRefundType}
          />
        )
      ) : step === 2 ? (
        <RefundTOS
          applicationId={application.applicationId}
          step={step}
          setStep={setStep}
        />
      ) : (
        refundType === 'Advance Funding' ? (
          step === 3 ? (
            <AdvancePaymentDetails
              interestAmount={interestAmount}
              lienAmount={lienAmount}
              step={step}
              setStep={setStep}
              _setLoanInterestAmount={setLoanInterestAmount}
            />
          ) : (
            <AdvancePaymentBankVerification
              step={step}
              setStep={setStep}
              setReload={setReload}
              handleInitApplication={handleInitApplication}
            />
          )
        ) : (
          step === 3 ? (
            shippingOption === '' ? (
              <RefundShippingOptions
                refundType={refundType}
                step={step}
                setStep={setStep}
                setShippingOption={setShippingOption}
              />
            ) : shippingOption === 'Shippo' ? (
              <RefundShippo
                applicationId={application.applicationId}
                step={step}
                refundType={refundType}
                setStep={setStep}
                setShippingOption={setShippingOption}
                handleGeneratedShippoLabel={null}
              />
            ) : (
              <></>
            )
          ) : (
            <RefundGenerateInvoice
              refundRate={refundRate}
              refundType={refundType}
            />
          )
        )
      )}
    </div>
  )
}